<template>
  <div class="rounded-lg border border-gray-200 overflow-hidden bg-white">
    <div class="flex justify-between items-center p-6">
      <h2 class="text-lg font-semibold text-gray-900">Statement</h2>
      <div class="flex gap-2">
        <Button @click="exportPaymentsToCSV" variant="outline">
          <Download class="h-4 w-4" />
        </Button>
      </div>
    </div>
    <div class="border-t border-gray-200">
      <table v-if="payments && payments.length > 0" class="w-full table-auto text-sm text-left">
        <thead class="bg-grey-light-100 text-grey-light-600 font-medium border-b border-gray-200">
          <tr>
            <th class="px-4 py-2">Payment Date</th>
            <th class="px-4 py-2">Type</th>
            <th class="px-4 py-2">Method</th>
            <th class="px-4 py-2">Provider</th>
            <th class="px-4 py-2 text-right">Amount</th>
            <th class="px-4 py-2">Status</th>
          </tr>
        </thead>
        <tbody class="text-grey-light-600">
          <template v-for="payment in payments" :key="payment.id">
            <tr class="border-b border-gray-200">
              <td class="p-5 flex items-center gap-2">{{ payment.date }}</td>
              <td class="p-5">{{ payment.type }}</td>
              <td class="p-5">{{ payment.method }}</td>
              <td class="p-5">{{ payment.provider }}</td>
              <td class="p-5 text-right">{{ displayCurrency(payment.amount) }}</td>
              <td class="p-5">
                <Badge :variant="getPaymentStatusVariant(payment.status)">
                  {{ payment.status }}
                </Badge>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
      <div v-else class="p-6 text-gray-500">
        <p>Statement not available.</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { IPaymentReport } from '@/models/payments';
import { displayCurrency, formatDateShort } from '@/utils/format.utils';
import { getPaymentStatusVariant } from '@/utils/payment.utils';
import { generatePaymentsCsv } from '@/utils/paymentPlan.utils';
import { Download } from 'lucide-vue-next';
import type { ModelOrderResponse } from '@layup/api-sdk';

const props = defineProps<{
  payments: IPaymentReport[];
  order?: ModelOrderResponse;
  orderState: string;
}>();

const exportPaymentsToCSV = () => {
  if (!props.payments || props.payments.length === 0) {
    console.warn('No payment data available for export');
    return;
  }

  const csvContent = generatePaymentsCsv(props.payments);
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `${props.order?._id}-${props.order?.reference}-payments.csv`);
  link.click();
  URL.revokeObjectURL(url);
};
</script>
